import type { ReactNode } from 'react'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useLogout } from '../api/hooks.js'
import { QueueIndicator } from './QueueIndicator.js'

/**
 * The frame.
 *
 * Bottom tab bar under 768px, top rail above it — a phone is held at the
 * bottom, a laptop is read from the top. Both use the same five destinations in
 * the same order, so the muscle memory transfers.
 *
 * The accent appears on exactly one tab at a time and nowhere else in the
 * chrome. That is the whole reason it still means something.
 */

const TABS = [
  // `also` keeps the tab lit on its detail routes — /week/2026-09-09 is still
  // the Week tab, and a nav that goes dark when you navigate within it reads
  // as being lost.
  { to: '/', label: 'Week', end: true, also: '/week' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/work', label: 'Work' },
  { to: '/exams', label: 'Exams' },
  { to: '/grades', label: 'Grades' },
] as const

export function Shell({ children, review }: { children: ReactNode; review?: number }) {
  const { pathname } = useLocation()
  const matchesAlso = (tab: (typeof TABS)[number]) =>
    'also' in tab && typeof tab.also === 'string' && pathname.startsWith(tab.also)

  return (
    <div className="min-h-dvh bg-[var(--bg)]">
      {/* header ---------------------------------------------------------- */}
      <header className="safe-top safe-x sticky top-0 z-20 border-b border-[var(--rule)] bg-[var(--bg)]/92 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 pb-2">
          <div className="min-w-0">
            <div className="tabular text-[10px] uppercase tracking-[0.18em] text-[var(--text-subtle)]">
              Fall 2026
            </div>
            <div className="truncate text-[15px] font-semibold tracking-tight text-[var(--text)]">
              Semester tracker
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <QueueIndicator />
            <NavLink
              to="/review"
              className="tap tabular relative flex items-center rounded-[var(--radius)] border border-[var(--border)] px-2.5 text-[11px] uppercase tracking-[0.08em] text-[var(--text-muted)]"
            >
              Review
              {review !== undefined && review > 0 && (
                <span className="ml-1.5 rounded-full bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--accent-contrast)]">
                  {review}
                </span>
              )}
            </NavLink>
            <SignOut />
          </div>
        </div>

        {/* top rail, 768px and up */}
        <nav className="mx-auto hidden max-w-6xl gap-1 px-4 md:flex">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={'end' in tab ? tab.end : false}
              className={({ isActive }) =>
                `tabular border-b-2 px-3 py-2 text-[12px] uppercase tracking-[0.1em] transition-colors ${
                  isActive || matchesAlso(tab)
                    ? 'border-[var(--accent)] text-[var(--accent)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]'
                }`
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* content --------------------------------------------------------- */}
      <main className="safe-x mx-auto max-w-6xl px-4 pt-4 pb-28 md:pb-10">{children}</main>

      {/* bottom bar, under 768px ------------------------------------------ */}
      <nav className="safe-bottom safe-x fixed inset-x-0 bottom-0 z-20 border-t border-[var(--border)] bg-[var(--surface)] md:hidden">
        <div className="mx-auto flex max-w-2xl">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={'end' in tab ? tab.end : false}
              className={({ isActive }) =>
                `tap tabular flex flex-1 flex-col items-center justify-center gap-1 pt-2 pb-1 text-[10px] uppercase tracking-[0.08em] ${
                  isActive || matchesAlso(tab) ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className="h-[3px] w-6 rounded-full"
                    style={{
                      backgroundColor: isActive || matchesAlso(tab) ? 'var(--accent)' : 'transparent',
                    }}
                  />
                  {tab.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}

/**
 * Sign out, in two taps.
 *
 * It sits next to the Review pill, which is somewhere I tap constantly, so a
 * single-tap sign-out here would eventually cost me a password entry mid-week
 * for nothing. The second tap is the whole safeguard: the first only asks.
 */
function SignOut() {
  const [confirming, setConfirming] = useState(false)
  const logout = useLogout()

  if (confirming) {
    return (
      <button
        type="button"
        onClick={() => logout.mutate()}
        onBlur={() => setConfirming(false)}
        autoFocus
        className="tap tabular rounded-[var(--radius)] border border-[var(--overdue)]/40 bg-[var(--overdue-wash)] px-2.5 text-[11px] uppercase tracking-[0.06em] text-[var(--overdue)]"
      >
        {logout.isPending ? '…' : 'Sign out?'}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      aria-label="Sign out"
      className="tap flex items-center justify-center rounded-[var(--radius)] border border-[var(--border)] px-2 text-[var(--text-muted)]"
    >
      <svg viewBox="0 0 14 14" className="h-4 w-4" aria-hidden>
        <path
          d="M5.5 2.5h-3v9h3M8 4.5 10.5 7 8 9.5M10.5 7H5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}
